import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { CapabilityCard, RoadmapStatus } from "@/api/capabilities";

/** Requests, votes and the FW list as the Ask panel sees them
 *  (`app/api/requests.py`). Filing a request is `useSubmitFeedback`. */
export type RoadmapMatch = {
  fw: number;
  title: string;
  description: string;
  status: RoadmapStatus;
  votes: number;
  voted: boolean;
};

export type RequestStatus = "new" | "seen" | "done" | "answered" | "linked" | "declined";

export type MyRequest = {
  id: string;
  created_at: string;
  body: string;
  source: string;
  status: RequestStatus;
  answer: CapabilityCard | null;
  item: { fw: number; title: string; description: string; status: RoadmapStatus } | null;
  decline_reason: string | null;
};

export type MyRequests = { requests: MyRequest[]; votes: RoadmapMatch[] };

export const MY_REQUESTS_KEY = ["requests", "mine"] as const;

const KNOWN_CODES = [
  "request_not_found",
  "request_already_handled",
  "roadmap_item_not_found",
  "roadmap_item_closed",
  "requests_disabled",
] as const;

/** The i18n key for a known request refusal; null for anything else. */
export function requestErrorKey(err: unknown): string | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  return typeof detail === "string" && (KNOWN_CODES as readonly string[]).includes(detail)
    ? `ask.errors.${detail}`
    : null;
}

export function useMyRequests(lang: string, enabled: boolean) {
  return useQuery({
    queryKey: [...MY_REQUESTS_KEY, lang],
    queryFn: async () => (await api.get<MyRequests>("/requests/mine", { params: { lang } })).data,
    enabled,
    retry: false,
  });
}

export function useWithdrawRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/requests/${encodeURIComponent(id)}`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["requests"] }),
  });
}

export function useRoadmapMatch(q: string, lang: string, enabled: boolean) {
  return useQuery({
    queryKey: ["roadmap", "match", q, lang],
    queryFn: async () =>
      (await api.get<{ items: RoadmapMatch[] }>("/roadmap/match", { params: { q, lang } })).data.items,
    enabled: enabled && q.length >= 3,
    staleTime: 30_000,
    retry: false,
  });
}

export function useVote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { fw: number; on: boolean }) => {
      if (p.on) await api.put(`/roadmap/${p.fw}/vote`, {});
      else await api.delete(`/roadmap/${p.fw}/vote`);
    },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["requests"] }),
        qc.invalidateQueries({ queryKey: ["roadmap"] }),
      ]),
  });
}
